import React, { useState, useEffect } from 'react';
import { DocumentationItem } from '../../types';
import { ChevronLeft, ChevronRight, Clock, Tag, Calendar, BookOpen, FileText, Zap } from 'lucide-react';
import '../../styles/DocumentationViewer.css';

interface DocumentationViewerProps {
  docItem: DocumentationItem;
  onBack: () => void;
}

const DocumentationViewer: React.FC<DocumentationViewerProps> = ({ docItem, onBack }) => {
  const [activeFile, setActiveFile] = useState(docItem.files[0]);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadMarkdownFile = async () => {
      if (!activeFile) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${docItem.markdownPath}/${activeFile.name}`);
        if (!response.ok) {
          throw new Error(`Failed to load ${activeFile.name}`);
        }
        const content = await response.text();
        setMarkdownContent(content);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documentation');
        setMarkdownContent('# Error\n\nFailed to load documentation content.');
      } finally {
        setIsLoading(false);
      }
    };

    loadMarkdownFile();
  }, [activeFile, docItem.markdownPath]);

  // Reading progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setReadingProgress(Math.min(100, Math.max(0, scrollPercent)));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const renderMarkdown = (content: string) => {
    // Enhanced markdown-to-HTML conversion with better styling
    return content
      .replace(/^# (.*$)/gim, '<h1 class="docs-md-h1">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 class="docs-md-h2">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="docs-md-h3">$1</h3>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="docs-md-strong">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="docs-md-em">$1</em>')
      .replace(/```([^`]+)```/gim, '<pre class="docs-md-pre"><code class="docs-md-code-block">$1</code></pre>')
      .replace(/`([^`]+)`/gim, '<code class="docs-md-code">$1</code>')
      .replace(/^\- (.*$)/gim, '<li class="docs-md-li">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="docs-md-link" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/\n/gim, '<br/>');
  };

  const getCurrentFileIndex = () => {
    return docItem.files.findIndex(file => file.name === activeFile?.name);
  };

  const canNavigatePrevious = () => {
    return getCurrentFileIndex() > 0;
  };

  const canNavigateNext = () => {
    return getCurrentFileIndex() < docItem.files.length - 1;
  };

  const navigatePrevious = () => {
    const currentIndex = getCurrentFileIndex();
    if (currentIndex > 0) {
      setActiveFile(docItem.files[currentIndex - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navigateNext = () => {
    const currentIndex = getCurrentFileIndex();
    if (currentIndex < docItem.files.length - 1) {
      setActiveFile(docItem.files[currentIndex + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const estimatedReadTimeForFile = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  return (
    <div className={`docs-viewer ${isLoaded ? 'docs-viewer--loaded' : ''}`}>
      {/* Reading Progress Bar */}
      <div className="docs-viewer__progress-bar">
        <div 
          className="docs-viewer__progress-fill"
          style={{ width: `${readingProgress}%` }}
        ></div>
      </div>

      {/* Enhanced Header */}
      <header className="docs-viewer__header">
        <div className="docs-viewer__header-background">
          <div className="docs-viewer__header-particles"></div>
        </div>
        
        <div className="docs-viewer__header-content">
          <button 
            onClick={onBack}
            className="docs-viewer__back-btn"
            aria-label="Back to documentation list"
          >
            <ChevronLeft size={20} />
            <span>Back to Docs</span>
            <div className="docs-viewer__back-btn-shine"></div>
          </button>
          
          <div className="docs-viewer__title-section">
            <div className="docs-viewer__emoji-container">
              <div className="docs-viewer__emoji">{docItem.emoji}</div>
              <div className="docs-viewer__emoji-glow"></div>
            </div>
            <div className="docs-viewer__title-content">
              <h1 className="docs-viewer__title">{docItem.title}</h1>
              <p className="docs-viewer__description">{docItem.description}</p>
              <div className="docs-viewer__breadcrumb">
                <BookOpen size={14} />
                <span>Documentation</span>
                <ChevronRight size={14} />
                <span>{docItem.title}</span>
                <ChevronRight size={14} />
                <span className="docs-viewer__breadcrumb-active">{activeFile?.title}</span>
              </div>
            </div>
          </div>

          <div className="docs-viewer__meta">
            <div className="docs-viewer__meta-item">
              <Clock size={16} />
              <span>{docItem.estimatedReadTime}</span>
            </div>
            {docItem.difficulty && (
              <div className={`docs-viewer__difficulty docs-viewer__difficulty--${docItem.difficulty}`}>
                <Zap size={12} />
                {docItem.difficulty}
              </div>
            )}
            {docItem.version && (
              <div className="docs-viewer__version">
                <FileText size={12} />
                v{docItem.version}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="docs-viewer__container">
        {/* Enhanced Sidebar */}
        <aside className="docs-viewer__sidebar">
          <div className="docs-viewer__sidebar-content">
            <div className="docs-viewer__toc">
              <h3 className="docs-viewer__toc-title">
                <BookOpen size={18} />
                Table of Contents
                <span className="docs-viewer__toc-count">{docItem.files.length} sections</span>
              </h3>
              <nav className="docs-viewer__toc-nav">
                {docItem.files.map((file, index) => (
                  <button
                    key={file.name}
                    onClick={() => setActiveFile(file)}
                    className={`docs-viewer__toc-item ${activeFile?.name === file.name ? 'docs-viewer__toc-item--active' : ''}`}
                  >
                    <span className="docs-viewer__toc-number">{file.order}</span>
                    <div className="docs-viewer__toc-content">
                      <span className="docs-viewer__toc-title">{file.title}</span>
                      {activeFile?.name === file.name && markdownContent && (
                        <span className="docs-viewer__toc-estimate">
                          {estimatedReadTimeForFile(markdownContent)}
                        </span>
                      )}
                    </div>
                    {activeFile?.name === file.name && (
                      <div className="docs-viewer__toc-active-indicator"></div>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Enhanced Tags */}
            <div className="docs-viewer__tags">
              <h4 className="docs-viewer__tags-title">
                <Tag size={16} />
                Tags
              </h4>
              <div className="docs-viewer__tags-list">
                {docItem.tags.map((tag) => (
                  <span key={tag} className="docs-viewer__tag">
                    <Tag size={10} />
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Last Updated */}
            <div className="docs-viewer__updated">
              <Calendar size={16} />
              <div>
                <span className="docs-viewer__updated-label">Last Updated</span>
                <span className="docs-viewer__updated-date">
                  {new Date(docItem.lastUpdated).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </aside>

        {/* Enhanced Main Content */}
        <main className="docs-viewer__main">
          <div className="docs-viewer__content">
            {isLoading && (
              <div className="docs-viewer__loading">
                <div className="docs-viewer__loading-spinner"></div>
                <p>Loading documentation...</p>
                <div className="docs-viewer__loading-progress">
                  <div className="docs-viewer__loading-progress-bar"></div>
                </div>
              </div>
            )}

            {error && (
              <div className="docs-viewer__error">
                <div className="docs-viewer__error-icon">⚠️</div>
                <h2>Error Loading Documentation</h2>
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="docs-viewer__retry-btn"
                >
                  <FileText size={16} />
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !error && (
              <>
                <div className="docs-viewer__file-header">
                  <div className="docs-viewer__file-header-content">
                    <h2 className="docs-viewer__file-title">{activeFile?.title}</h2>
                    <div className="docs-viewer__file-meta">
                      <span className="docs-viewer__file-name">{activeFile?.name}</span>
                      <span className="docs-viewer__file-section">
                        Section {activeFile?.order} of {docItem.files.length}
                      </span>
                      {markdownContent && (
                        <span className="docs-viewer__file-read-time">
                          <Clock size={12} />
                          {estimatedReadTimeForFile(markdownContent)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="docs-viewer__markdown-container">
                  <div 
                    className="docs-viewer__markdown"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(markdownContent) }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Enhanced Navigation */}
          <nav className="docs-viewer__pagination">
            <button
              onClick={navigatePrevious}
              disabled={!canNavigatePrevious()}
              className="docs-viewer__nav-btn docs-viewer__nav-btn--prev"
            >
              <ChevronLeft size={20} />
              <div className="docs-viewer__nav-content">
                <span className="docs-viewer__nav-label">Previous</span>
                {canNavigatePrevious() && (
                  <span className="docs-viewer__nav-title">
                    {docItem.files[getCurrentFileIndex() - 1]?.title}
                  </span>
                )}
              </div>
            </button>

            <div className="docs-viewer__nav-center">
              <div className="docs-viewer__nav-progress">
                <span className="docs-viewer__nav-progress-text">
                  {getCurrentFileIndex() + 1} of {docItem.files.length}
                </span>
                <div className="docs-viewer__nav-progress-bar">
                  <div 
                    className="docs-viewer__nav-progress-fill"
                    style={{ width: `${((getCurrentFileIndex() + 1) / docItem.files.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <button
              onClick={navigateNext}
              disabled={!canNavigateNext()}
              className="docs-viewer__nav-btn docs-viewer__nav-btn--next"
            >
              <div className="docs-viewer__nav-content">
                <span className="docs-viewer__nav-label">Next</span>
                {canNavigateNext() && (
                  <span className="docs-viewer__nav-title">
                    {docItem.files[getCurrentFileIndex() + 1]?.title}
                  </span>
                )}
              </div>
              <ChevronRight size={20} />
            </button>
          </nav>
        </main>
      </div>
    </div>
  );
};

export default DocumentationViewer; 